import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarCheck,
  TrendingUp,
  Users,
  Activity,
  Clock,
  Plus,
  CreditCard,
  Calendar,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import SiteFilter from '@/components/admin/SiteFilter';
import { cn } from '@/lib/utils';

const SITE_LABELS = {
  specialist_dental_london: { label: 'SDL', color: 'bg-amber-100 text-amber-800' },
  london_dental_academy: { label: 'LDA', color: 'bg-blue-100 text-blue-800' },
  shihab_romeed: { label: 'SR', color: 'bg-stone-200 text-stone-800' },
};

export default function AdminDashboard() {
  const { apiFetch } = useAdmin();
  const { createDraft } = useBookingDraft();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    const q = siteFilter === 'all' ? '/dashboard/stats' : `/dashboard/stats?siteKey=${siteFilter}`;
    apiFetch(q)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [apiFetch, siteFilter]);

  const kpis = [
    {
      title: 'Bookings Today',
      value: data?.stats?.todayBookingsCount || 0,
      icon: CalendarCheck,
      gradient: 'from-stone-800 to-stone-900',
    },
    {
      title: 'Revenue Today',
      value: data?.stats?.todayRevenue || 0,
      prefix: '£',
      icon: TrendingUp,
      gradient: 'from-emerald-700 to-emerald-800',
    },
    {
      title: 'New Patients (Week)',
      value: data?.stats?.newPatientsThisWeek || 0,
      icon: Users,
      gradient: 'from-blue-600 to-blue-700',
    },
    {
      title: 'Pending Revenue',
      value: data?.stats?.outstandingBalance || 0,
      prefix: '£',
      icon: Clock,
      gradient: 'from-amber-600 to-amber-700',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-3xl font-playfair font-medium text-stone-900 tracking-tight">
            Dashboard
          </h2>
          <p className="text-stone-500 mt-1">
            Unified view across all clinics and academy.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SiteFilter value={siteFilter} onChange={setSiteFilter} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => createDraft()} className="h-9 gap-2 bg-stone-900 text-white hover:bg-stone-800 text-sm">
          <Plus className="w-3.5 h-3.5" /> New Booking
        </Button>
        <Link to="/admin/calendar">
          <Button variant="outline" className="h-9 gap-2 text-sm border-stone-200">
            <Calendar className="w-3.5 h-3.5" /> Today's Schedule
          </Button>
        </Link>
        <Link to="/admin/payments">
          <Button variant="outline" className="h-9 gap-2 text-sm border-stone-200">
            <CreditCard className="w-3.5 h-3.5" /> Pending Payments
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <Card className={`relative overflow-hidden bg-gradient-to-br ${kpi.gradient} text-white border-0 shadow-lg`}>
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-white/70">{kpi.title}</p>
                    <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold tracking-tight">
                    {loading ? (
                      <Skeleton className="h-9 w-24 bg-white/20" />
                    ) : (
                      <span>{kpi.prefix}{kpi.value.toLocaleString()}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* 7-Day Bookings Chart */}
      {!loading && data?.chartData?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium text-stone-900">Bookings — Last 7 Days</CardTitle>
            </CardHeader>
            <CardContent className="pt-2 pb-5">
              <div className="flex items-end justify-between gap-2 h-32">
                {data.chartData.map((d, i) => {
                  const max = Math.max(...data.chartData.map(c => c.bookings), 1);
                  const h = (d.bookings / max) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                      <span className="text-[10px] font-semibold text-stone-600">{d.bookings}</span>
                      <div className="w-full max-w-[32px] rounded-t-md bg-stone-900 transition-all" style={{ height: `${Math.max(h, 4)}%` }} />
                      <span className="text-[10px] text-stone-400 font-medium">{d.date}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming Bookings */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="text-lg font-medium text-stone-900">Upcoming Confirmed Bookings</h3>
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-3">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : !data?.upcomingBookings?.length ? (
                <div className="p-12 text-center text-stone-500">No upcoming confirmed bookings.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 bg-stone-50/50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Patient</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Service</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Date/Time</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Site</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {data.upcomingBookings.map((b, i) => (
                        <tr key={b.id} className="hover:bg-stone-50/50">
                          <td className="px-5 py-3">
                            <p className="font-medium text-stone-900">{b.patient?.firstName} {b.patient?.lastName}</p>
                            <p className="text-xs text-stone-400">{b.patient?.phone}</p>
                          </td>
                          <td className="px-5 py-3 text-stone-600">{b.service}</td>
                          <td className="px-5 py-3">
                            <p className="font-medium text-stone-900">{new Date(b.confirmedDate).toLocaleDateString()}</p>
                            <p className="text-xs text-stone-500">{b.confirmedTime}</p>
                          </td>
                          <td className="px-5 py-3">
                            <span className={cn('px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider', SITE_LABELS[b.siteKey]?.color || 'bg-stone-100')}>
                              {SITE_LABELS[b.siteKey]?.label || b.siteKey}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-stone-900">Recent Activity</h3>
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-5">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : !data?.recentActivity?.length ? (
                <div className="text-center text-stone-500 py-8 text-sm">No recent activity.</div>
              ) : (
                <div className="space-y-4 relative before:absolute before:inset-0 before:ml-2.5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-stone-200 before:to-transparent">
                  {data.recentActivity.map((log) => (
                    <div key={log.id} className="relative flex items-start gap-4">
                      <div className="absolute left-0 w-5 h-5 rounded-full bg-stone-100 border border-stone-300 flex items-center justify-center mt-0.5 z-10">
                        <Activity className="w-2.5 h-2.5 text-stone-500" />
                      </div>
                      <div className="ml-8 w-full bg-white relative z-20 pb-2">
                        <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-0.5">{new Date(log.timestamp).toLocaleString()}</p>
                        <p className="text-sm font-medium text-stone-900">{log.action}</p>
                        {log.details && <p className="text-sm text-stone-600 mt-0.5">{log.details}</p>}
                        <p className="text-[10px] text-stone-400 mt-1 uppercase tracking-wider">{log.actorEmail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
